/**
 * GatedPanel — what a learner sees at the edge of the free tier.
 *
 * FOUNDER, 2026-08-13: "now make the app show the 402 as an enrol prompt", then "do practice too."
 *
 * ── WHY THIS IS A COMPONENT AND NOT A SECOND COPY ─────────────────────────────────────────────
 * It is the same moment on both surfaces — the learner deciding whether to enrol — and the two would
 * drift. Not cosmetically: they would drift in what they PROMISE. One saying "the foundation tier is
 * free" while the other said "the first seven chapters are free" would be two different offers from
 * one product, and the one a learner remembers is whichever he read first.
 *
 * ── IT QUOTES THE ENGINE, IT DOES NOT PARAPHRASE IT ───────────────────────────────────────────
 * `reason` and `free_instead` are rendered as the engine wrote them. Only the server knows which
 * track was asked for, which tier is free on THAT ladder, and whether the caller is signed in — and
 * a friendlier sentence written here would be a second source of truth about what is being sold.
 * The only thing this file decides is layout, and what to say about the next step.
 *
 * ── IT NEVER CLAIMS A PRICE ───────────────────────────────────────────────────────────────────
 * No track on this ladder has a declared price yet. A panel that invented "from $X" would be
 * inventing a commercial fact, which is the same offence as inventing a technical one (FP-018).
 * It says what is free and how to carry a record; the founder's pricing goes here when it exists.
 */
import Enrol from './enrol.jsx';

export default function GatedPanel({ envelope, fallbackTitle = null, what = 'This chapter' }) {
  if (!envelope || envelope.state !== 'gated') return null;
  const chapter = envelope.chapter ?? null;
  const title = envelope.title || fallbackTitle;
  return (
    <div className="learn-gated">
      <div className="g-label">Enrolled learners</div>
      {/* Three shapes, because the routes carry different things: /api/reading sends a title,
          /api/mcq sends only a chapter number, and the free-form routes send neither. Dropping the
          chapter when a title is absent — which the first draft did — left a candidate on Practice
          reading "This paper is for enrolled learners" with no idea WHICH paper he had reached. */}
      <h3 className="g-h3 learn-p-title">
        {title ? `${chapter != null ? `Chapter ${chapter} — ` : ''}${title}`
          : chapter != null ? `Chapter ${chapter} is for enrolled learners`
          : `${what} is for enrolled learners`}
      </h3>
      <p className="g-body">{envelope.reason}</p>
      {envelope.free_instead && <p className="g-body g-dim">{envelope.free_instead}</p>}
      {/* ── THE FREE CHAPTER HAS A PUBLIC COPY, SO SAY WHERE ─────────────────────────────────
          This panel has been telling a stranger the chapter costs nothing and then offering only
          an enrol form. That is a true sentence next to a closed door, and the commonest reason a
          free thing goes unread is somebody believing it was not really free. The server decides
          WHICH chapters have a public copy; this only renders what it was handed, so a chapter
          cannot be advertised here while the route still refuses it. */}
      {envelope.showcase && (
        <p className="g-body">
          <a className="g-read-now" href={envelope.showcase}>Read this chapter now, without enrolling &rarr;</a>
        </p>
      )}
      {/* ── A FREE CHAPTER IS REFUSED DIFFERENTLY FROM A PAID ONE (founder, 2026-08-16) ────────
          "i thought enrolment was even to get free ai course, not anonymous." Free describes the
          FEE. So there are two refusals behind this panel and they must not read alike: one says
          "enrol, it costs nothing", the other says "this track needs an enrolment". A learner who
          is told to enrol for a chapter that is free, in the same words used for one that is not,
          will assume he is being asked to pay — and the commonest reason a free thing goes unread
          is somebody believing it was not free. */}
      <p className="g-body g-dim learn-gated-how">
        {envelope.named
          ? 'You are signed in — this identity holds no enrolment in this track yet.'
          : envelope.free_chapter
          ? 'Enrol below — it takes a name, a contact and a passphrase, and there is nothing to pay for this chapter.'
          : 'Sign in below, or enrol if this is your first time. You do not have to go anywhere else.'}
      </p>
      {/* ── THE REFUSAL CARRIES ITS REMEDY ────────────────────────────────────────────────────
          FOUNDER, 2026-08-16: "actually login and allowing shall be easy". This previously read
          "Enrol under Progress to carry your record" — an instruction to go and look for something,
          which is a dead end however politely it is worded. The founder hit it on his own paid
          ladder, twice, holding a valid grant the whole time.

          Chapter 9 of this very ladder states the rule it was breaking: a refusal that says only
          "blocked" will be read as the tool being broken; a refusal that says why, and what to do
          instead, is a rule. So the form is here, at the refusal.

          It opens on SIGN IN rather than enrol, because someone who has reached a gated chapter has
          usually been here before — and the form carries its own way across to enrolling. Nothing
          about who may see what has changed; this is the same gate, with the door handle attached. */}
      {!envelope.named && (
        <div className="learn-gated-enrol">
          <Enrol startMode="signin" />
        </div>
      )}
    </div>
  );
}
